/**
 * Lifetime jigsaw reward: one cartoon picture cut into four pieces whose bounding boxes match
 * config/puzzle-reward.js PUZZLE_PIECE_LAYOUT (piece 1 263×330, 2 330×263, 3 330×263, 4 263×330
 * out of a 520×520 picture).
 *
 * The picture is a sunny lagoon: a grinning sun in the upper left (where the light comes from),
 * a rainbow, a palm island, the starfish on the beach, the cell bobbing in the water and the
 * next tiers rising out of the sea in their bubbles toward the rainbow. It is painted once into
 * a module-level canvas and each piece copies its share through a clip.
 *
 * The cut copies the retired cut-puzzle.py script exactly (TAB_R 0.16, TAB_D 0.10 of the edge, circular
 * heads whose neck is the chord where the head crosses the edge, the same tab sides), so the
 * pieces interlock with no gap at the layout's positions. Each piece gets an ink line and a
 * bevel inside its own jigsaw edge, so assembled seams read as two inked edges meeting.
 */
import { PUZZLE_PIECE_LAYOUT } from "../../../config/puzzle-reward.js";
import { PUFF_TONES, LINE_BODY } from "../palette.js";
import {
  TAU, celShape, celEllipse, cuteFace, glint, inkStyle, paintPuff, PUFF_SHAPES, sparklePath,
  starburstPath, hash,
} from "../toon-kit.js";
import { CREATURES, drawCreature } from "../creatures/index.js";

/** Side of the whole picture, in the piece frames' logical units. */
export var PUZZLE_SIZE = 520;

var TAB_R = 0.16;
var TAB_D = 0.1;
var ARC_STEPS = 64;
/** Ink inside each piece edge, and the width of the bevel under it. */
var EDGE_INK = 3.4;
var BEVEL = 8;

var HORIZON = 300;
var SUN = [116, 112, 60];

// ---- the cut (port of the retired cut-puzzle.py) ------------------------------------------

function normAngle(a) {
  while (a <= -Math.PI) a += TAU;
  while (a > Math.PI) a -= TAU;
  return a;
}

/** Points from a to b along a jigsaw edge; the head bulges toward `side` (+1 / −1). */
function edgeCurve(a, b, side) {
  var dx = b[0] - a[0], dy = b[1] - a[1];
  var length = Math.hypot(dx, dy);
  var ux = dx / length, uy = dy / length;
  var nx = -uy, ny = ux;
  var mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
  var r = TAB_R * length, d = TAB_D * length;
  var neck = Math.sqrt(Math.max(r * r - d * d, 1e-9));
  function local(along, perp) {
    return [mx + ux * along + nx * perp, my + uy * along + ny * perp];
  }
  var cc = local(0, side * d);
  var start = local(-neck, 0), end = local(neck, 0);
  var a0 = Math.atan2(start[1] - cc[1], start[0] - cc[0]);
  var a1 = Math.atan2(end[1] - cc[1], end[0] - cc[0]);
  var apex = local(0, side * (d + r));
  var am = Math.atan2(apex[1] - cc[1], apex[0] - cc[0]);
  function sweep(target, dir) {
    var delta = normAngle(target - a0);
    if (dir < 0 && delta > 0) delta -= TAU;
    if (dir > 0 && delta < 0) delta += TAU;
    return delta;
  }
  // The sweep that passes through the head's apex (the major arc).
  var dEnd = 0;
  for (var dir = 1; dir >= -1; dir -= 2) {
    dEnd = sweep(a1, dir);
    if (Math.abs(sweep(am, dir)) < Math.abs(dEnd)) break;
  }
  var pts = [a.slice(), start];
  for (var i = 1; i < ARC_STEPS; i++) {
    var ang = a0 + dEnd * (i / ARC_STEPS);
    pts.push([cc[0] + r * Math.cos(ang), cc[1] + r * Math.sin(ang)]);
  }
  pts.push(end, b.slice());
  return pts;
}

var POLYS = null;

/** The four piece outlines in picture units, in PUZZLE_PIECE_LAYOUT order (tl, tr, bl, br). */
export function puzzlePiecePolygons() {
  if (POLYS) return POLYS;
  var W = PUZZLE_SIZE, H = PUZZLE_SIZE, cx = W / 2, cy = H / 2;
  var eTop = edgeCurve([cx, 0], [cx, cy], 1);
  var eBottom = edgeCurve([cx, cy], [cx, H], -1);
  var eLeft = edgeCurve([0, cy], [cx, cy], 1);
  var eRight = edgeCurve([cx, cy], [W, cy], -1);
  function rev(p) { return p.slice().reverse(); }
  POLYS = [
    [[0, 0], [cx, 0]].concat(eTop, rev(eLeft), [[0, cy]]),
    [[cx, 0], [W, 0], [W, cy]].concat(rev(eRight), rev(eTop)),
    [[0, cy]].concat(eLeft, eBottom, [[cx, H], [0, H]]),
    eRight.concat([[W, cy], [W, H], [cx, H]], rev(eBottom)),
  ];
  return POLYS;
}

function tracePoly(c, pts) {
  c.beginPath();
  c.moveTo(pts[0][0], pts[0][1]);
  for (var i = 1; i < pts.length; i++) c.lineTo(pts[i][0], pts[i][1]);
  c.closePath();
}

// ---- the picture ------------------------------------------------------------------------

var SCENE = null;
var SCENE_SCALE = 2;
/**
 * The 4 MB scene lives only while pieces are being cut from it: each piece is encoded once per
 * session for the DOM (and not kept as a canvas), either all at once by the puzzle board or one
 * per idle slice by the Merge warm-up. The scene is dropped a moment after the last cut and
 * repainted only if something asks again.
 */
var SCENE_KEEP_MS = 1500;
var sceneReleaseTimer = null;

function releaseSceneSoon() {
  if (typeof setTimeout !== "function") {
    SCENE = null;
    return;
  }
  if (sceneReleaseTimer != null) clearTimeout(sceneReleaseTimer);
  sceneReleaseTimer = setTimeout(function () {
    sceneReleaseTimer = null;
    SCENE = null;
  }, SCENE_KEEP_MS);
}

/**
 * The whole 520×520 picture, painted at 2× (the pieces' DOM encode scale) and shared by the
 * pieces cut in the same task. Null without a 2D canvas.
 */
export function getPuzzleSceneCanvas() {
  if (SCENE) return SCENE;
  if (typeof document === "undefined") return null;
  var cv = document.createElement("canvas");
  cv.width = cv.height = PUZZLE_SIZE * SCENE_SCALE;
  // CPU-backed: it is only read back into the pieces' PNG encodes.
  var c = cv.getContext && (cv.getContext("2d", { willReadFrequently: true }) || cv.getContext("2d"));
  if (!c) return null;
  c.scale(SCENE_SCALE, SCENE_SCALE);
  drawPuzzleScene(c);
  SCENE = cv;
  return SCENE;
}

/** Paint the picture into `c` (520×520 logical units from the origin). */
export function drawPuzzleScene(c) {
  c.save();
  c.lineJoin = "round";
  c.lineCap = "round";
  sky(c);
  rainbow(c);
  sun(c);
  cloud(c, 276, 70, 46, 0, 1);
  cloud(c, 500, 150, 50, 2, -1);
  cloud(c, 34, 248, 34, 1, 1);
  cloud(c, 270, 288, 36, 1, -1);
  sea(c);
  sailboat(c, 196, HORIZON - 4);
  risingBubbles(c);
  cellInWater(c, 248, 352);
  beach(c);
  palm(c);
  sandcastle(c, 236, 440);
  bucket(c, 322, 470);
  shell(c, 38, 492, 0.3, "#ff9ab8");
  shell(c, 404, 492, -0.25, "#ffc0a0");
  pebble(c, 168, 498, 7);
  pebble(c, 326, 506, 6);
  starfishOnSand(c, 98, 454);
  sparkles(c);
  c.restore();
}

function sky(c) {
  c.save();
  c.beginPath();
  c.rect(0, 0, PUZZLE_SIZE, HORIZON + 2);
  c.clip();
  c.fillStyle = "#47b3ec";
  c.fillRect(0, 0, PUZZLE_SIZE, HORIZON + 2);
  // Hard-edged halo rings round the sun: the toon way to say "bright, warm sky".
  var rings = [[300, "#5ec2f1"], [228, "#7ccff5"], [166, "#a0def9"], [116, "#d4f1fb"], [90, "#fff3c4"]];
  for (var i = 0; i < rings.length; i++) {
    c.beginPath();
    c.arc(SUN[0], SUN[1], rings[i][0], 0, TAU);
    c.fillStyle = rings[i][1];
    c.fill();
  }
  c.restore();
}

function rainbow(c) {
  var cx = 452, cy = 318, outer = 196, band = 12;
  var colours = ["#ff7a7e", "#ffad55", "#ffdc78", "#7fda86", "#72b4ff", "#b9a2ff"];
  c.save();
  c.beginPath();
  c.rect(0, 0, PUZZLE_SIZE, HORIZON);
  c.clip();
  for (var i = 0; i < colours.length; i++) {
    c.beginPath();
    c.arc(cx, cy, outer - band * (i + 0.5), Math.PI, TAU);
    c.strokeStyle = colours[i];
    c.lineWidth = band + 0.6;
    c.lineCap = "butt";
    c.stroke();
  }
  c.beginPath();
  c.arc(cx, cy, outer, Math.PI, TAU);
  c.moveTo(cx - outer + band * colours.length, cy);
  c.arc(cx, cy, outer - band * colours.length, Math.PI, TAU);
  inkStyle(c, 3.5);
  c.lineCap = "butt";
  c.stroke();
  // A lit streak along the top of the red band.
  c.beginPath();
  c.arc(cx, cy, outer - 3.5, Math.PI * 1.22, Math.PI * 1.42);
  c.strokeStyle = "rgba(255,255,255,0.7)";
  c.lineWidth = 3;
  c.lineCap = "round";
  c.stroke();
  c.restore();
}

function sun(c) {
  var x = SUN[0], y = SUN[1], r = SUN[2];
  // Rays: two staggered starbursts, the back one warmer.
  starburstPath(c, x, y, r * 1.62, { n: 12, inner: 0.7, seed: 11, rot: 0.13 });
  c.fillStyle = "#ffb03a";
  c.fill();
  inkStyle(c, 4);
  c.stroke();
  starburstPath(c, x, y, r * 1.36, { n: 12, inner: 0.74, seed: 4, rot: 0.13 + Math.PI / 12 });
  c.fillStyle = "#ffd45c";
  c.fill();
  inkStyle(c, 3.5);
  c.stroke();
  celEllipse(c, x, y, r, r, { base: "#ffd84a", light: "#fff6c4", shade: "#ffa62e" }, {
    line: 5,
    glint: false,
    lightPath: function (cc) { cc.ellipse(x - r * 0.34, y - r * 0.38, r * 0.5, r * 0.36, -0.6, 0, TAU); },
  });
  glint(c, x - r * 0.5, y - r * 0.52, r * 0.13);
  cuteFace(c, x + 2, y + r * 0.12, r * 0.9, { mood: "grin", eye: 0.16, gap: 0.6, mouth: 0.2 });
}

function cloud(c, x, y, R, shape, flip) {
  c.save();
  c.translate(x, y);
  c.scale(R * flip, R * 0.78);
  paintPuff(c, PUFF_TONES.cloud, PUFF_SHAPES[shape]);
  c.restore();
}

function sea(c) {
  var bands = [[HORIZON, "#7fd3f4"], [HORIZON + 14, "#55bdeb"], [HORIZON + 44, "#38a6dd"], [HORIZON + 112, "#2b93cf"]];
  for (var i = 0; i < bands.length; i++) {
    c.fillStyle = bands[i][1];
    c.fillRect(0, bands[i][0], PUZZLE_SIZE, PUZZLE_SIZE - bands[i][0]);
  }
  c.beginPath();
  c.moveTo(0, HORIZON);
  c.lineTo(PUZZLE_SIZE, HORIZON);
  inkStyle(c, 3, "#1f6f9f");
  c.stroke();
  // Sun glitter on the water under the sun.
  c.fillStyle = "#fff6c4";
  var glitter = [[0, 8, 34], [6, 20, 26], [-4, 34, 40], [4, 50, 22], [-2, 66, 30]];
  for (var g = 0; g < glitter.length; g++) {
    var gx = SUN[0] + glitter[g][0], gy = HORIZON + glitter[g][1], gw = glitter[g][2];
    c.beginPath();
    c.ellipse(gx, gy, gw / 2, 2.6, 0, 0, TAU);
    c.fill();
  }
  // Little wave ticks scattered over the water.
  c.strokeStyle = "rgba(255,255,255,0.85)";
  c.lineWidth = 3.2;
  for (var k = 0; k < 16; k++) {
    var wx = 20 + hash(k * 3.1) * 480, wy = HORIZON + 22 + hash(k * 7.7 + 2) * 110;
    if (Math.abs(wx - SUN[0]) < 40) continue;
    var ww = 9 + hash(k * 5.3) * 8;
    c.beginPath();
    c.moveTo(wx - ww, wy);
    c.quadraticCurveTo(wx - ww / 2, wy - 5, wx, wy);
    c.quadraticCurveTo(wx + ww / 2, wy - 5, wx + ww, wy);
    c.stroke();
  }
}

function sailboat(c, x, y) {
  c.save();
  c.translate(x, y);
  // Hull.
  c.beginPath();
  c.moveTo(-22, -6);
  c.lineTo(22, -6);
  c.quadraticCurveTo(18, 4, 12, 5);
  c.lineTo(-14, 5);
  c.quadraticCurveTo(-20, 3, -22, -6);
  c.closePath();
  c.fillStyle = "#ff5a5f";
  c.fill();
  inkStyle(c, 3);
  c.stroke();
  // Mast and sails.
  c.beginPath();
  c.moveTo(0, -6);
  c.lineTo(0, -46);
  c.stroke();
  c.beginPath();
  c.moveTo(3, -44);
  c.quadraticCurveTo(22, -26, 20, -10);
  c.lineTo(3, -10);
  c.closePath();
  c.fillStyle = "#ffffff";
  c.fill();
  c.stroke();
  c.beginPath();
  c.moveTo(-3, -38);
  c.lineTo(-3, -10);
  c.lineTo(-17, -10);
  c.closePath();
  c.fillStyle = "#ffd45c";
  c.fill();
  c.stroke();
  c.restore();
}

/**
 * The next tiers drifting up out of the sea in their bubbles toward the rainbow. A tier whose
 * drawer is not registered falls back to a plain soap bubble, so the picture holds either way.
 */
function risingBubbles(c) {
  soapBubble(c, 300, 252, 7);
  soapBubble(c, 368, 196, 6);
  soapBubble(c, 434, 106, 8);
  bubble(c, 322, 300, 20, "jellyfish", 0.2);
  bubble(c, 356, 236, 25, "fish", -0.15);
  bubble(c, 312, 166, 28, "snail", 0.1);
  bubble(c, 386, 98, 32, "butterfly", -0.1);
}

function bubble(c, x, y, r, assetId, rot) {
  if (!CREATURES[assetId]) {
    soapBubble(c, x, y, r);
    return;
  }
  c.save();
  c.translate(x, y);
  c.rotate(rot || 0);
  drawCreature(c, assetId, r, { t: 0, mood: "happy" });
  c.restore();
  // An ink ring at the picture's weight, so small bubbles hold their edge.
  c.beginPath();
  c.arc(x, y, r, 0, TAU);
  inkStyle(c, Math.max(2.6, r * LINE_BODY * 1.4));
  c.stroke();
}

function soapBubble(c, x, y, r) {
  c.beginPath();
  c.arc(x, y, r, 0, TAU);
  c.fillStyle = "rgba(255,255,255,0.28)";
  c.fill();
  inkStyle(c, Math.max(2, r * 0.16), "#2f7fb8");
  c.stroke();
  c.fillStyle = "#ffffff";
  c.beginPath();
  c.ellipse(x - r * 0.38, y - r * 0.4, r * 0.3, r * 0.18, -0.7, 0, TAU);
  c.fill();
}

function cellInWater(c, x, y) {
  // Ripple behind, the cell, then the front of the ripple over its belly.
  c.beginPath();
  c.ellipse(x, y + 20, 44, 10, 0, Math.PI, TAU);
  inkStyle(c, 3.5, "rgba(255,255,255,0.9)");
  c.stroke();
  if (CREATURES.cell) {
    c.save();
    c.translate(x, y);
    c.rotate(-0.12);
    drawCreature(c, "cell", 50, { t: 0, noDisc: true, mood: "happy" });
    c.restore();
  } else {
    soapBubble(c, x, y, 30);
  }
  c.save();
  c.beginPath();
  c.ellipse(x, y + 20, 44, 10, 0, 0, Math.PI);
  c.lineTo(x - 50, y + 60);
  c.lineTo(x + 50, y + 60);
  c.closePath();
  c.clip();
  c.fillStyle = "#38a6dd";
  c.fillRect(x - 50, y + 18, 100, 30);
  c.restore();
  c.beginPath();
  c.ellipse(x, y + 20, 44, 10, 0, 0.08, Math.PI - 0.08);
  inkStyle(c, 3.5, "#ffffff");
  c.stroke();
}

var SAND = { base: "#ffd98a", light: "#fff1c8", shade: "#ecb862" };

function beachPath(c, g) {
  c.moveTo(-20, 404 - g);
  c.bezierCurveTo(70, 376 - g, 176, 386 - g, 256, 412 - g);
  c.bezierCurveTo(330, 436 - g, 396, 392 - g, 540, 396 - g);
  c.lineTo(540, 540);
  c.lineTo(-20, 540);
  c.closePath();
}

function beach(c) {
  // Foam along the shore: the beach edge lifted, in white.
  c.save();
  c.translate(0, -7);
  c.beginPath();
  beachPath(c, 0);
  c.fillStyle = "#e8f8ff";
  c.fill();
  inkStyle(c, 3, "#3a7aa0");
  c.stroke();
  c.restore();
  celShape(c, beachPath, SAND, 140, {
    line: 4.5,
    shift: [-0.02, -0.06],
    lightPath: function (cc) { cc.ellipse(120, 400, 150, 18, -0.08, 0, TAU); },
  });
  // Sand grains.
  c.fillStyle = "#e3a957";
  for (var i = 0; i < 26; i++) {
    var x = hash(i * 4.3 + 1) * 520, y = 424 + hash(i * 9.1 + 3) * 90;
    c.beginPath();
    c.arc(x, y, 1.6 + hash(i) * 1.6, 0, TAU);
    c.fill();
  }
}

var LEAF = { base: "#4fc35c", light: "#a4ec8e", shade: "#2e9a42" };
var TRUNK = { base: "#c98a4a", light: "#eab27a", shade: "#94592a" };

function trunkPoint(t) {
  // Quadratic from the foot (470, 462) through a lean to the crown (440, 318).
  var p0 = [470, 462], p1 = [490, 380], p2 = [440, 318];
  var u = 1 - t;
  return [u * u * p0[0] + 2 * u * t * p1[0] + t * t * p2[0], u * u * p0[1] + 2 * u * t * p1[1] + t * t * p2[1]];
}

function trunkPath(c, g) {
  var n = 14, left = [], right = [];
  for (var i = 0; i <= n; i++) {
    var t = i / n, p = trunkPoint(t), q = trunkPoint(Math.min(1, t + 0.01)), o = trunkPoint(Math.max(0, t - 0.01));
    var dx = q[0] - o[0], dy = q[1] - o[1], len = Math.hypot(dx, dy) || 1;
    var nx = -dy / len, ny = dx / len, hw = 16 - 6 * t + g;
    left.push([p[0] + nx * hw, p[1] + ny * hw]);
    right.push([p[0] - nx * hw, p[1] - ny * hw]);
  }
  c.moveTo(left[0][0], left[0][1]);
  for (var a = 1; a < left.length; a++) c.lineTo(left[a][0], left[a][1]);
  for (var b = right.length - 1; b >= 0; b--) c.lineTo(right[b][0], right[b][1]);
  c.closePath();
}

/** One frond leaving the crown at angle `a`, `len` long, drooping by `droop`. */
function leaf(c, a, len, droop, wid) {
  c.save();
  c.translate(440, 318);
  c.rotate(a);
  var path = function (cc, g) {
    cc.moveTo(0, -4 - g);
    cc.quadraticCurveTo(len * 0.5, -wid - droop * 0.4 - g, len + g, droop);
    cc.quadraticCurveTo(len * 0.55, wid * 0.4 + droop * 0.9 + g, 0, 5 + g);
    cc.closePath();
  };
  celShape(c, path, LEAF, 34, { line: 3.8, shift: [-0.04, -0.12] });
  // Midrib and a couple of cuts on the blade.
  c.beginPath();
  c.moveTo(4, 0);
  c.quadraticCurveTo(len * 0.5, -droop * 0.1 + 1, len - 4, droop * 0.9);
  inkStyle(c, 2.4, "#2a7a36");
  c.stroke();
  c.restore();
}

function palm(c) {
  celShape(c, trunkPath, TRUNK, 40, { line: 4.2, shift: [-0.14, -0.04] });
  // Bark rings.
  c.save();
  c.beginPath();
  trunkPath(c, 0);
  c.clip();
  for (var i = 1; i < 7; i++) {
    var t = i / 7, p = trunkPoint(t);
    c.beginPath();
    c.ellipse(p[0], p[1], 18, 5, -0.18 + t * 0.5, 0.15, Math.PI - 0.15);
    inkStyle(c, 2.6, "#7a4420");
    c.stroke();
  }
  c.restore();
  // Fronds, back ones first.
  leaf(c, -2.75, 104, 30, 22);
  leaf(c, -0.45, 100, 32, 22);
  leaf(c, -1.95, 84, -10, 19);
  leaf(c, -1.15, 88, -8, 19);
  leaf(c, 2.72, 90, -32, 20);
  leaf(c, 0.36, 94, 30, 20);
  // Coconuts under the crown.
  var nuts = [[430, 330], [450, 334], [438, 346]];
  for (var n = 0; n < nuts.length; n++) {
    celEllipse(c, nuts[n][0], nuts[n][1], 10, 10, { base: "#8a5a32", light: "#c08a5a", shade: "#5e3a1c" }, { line: 3 });
  }
}

var CASTLE = { base: "#f6c86c", light: "#ffe7aa", shade: "#d79e44" };

function sandcastle(c, x, y) {
  c.save();
  c.translate(x, y);
  var body = function (cc, g) {
    // Wall with crenellations and a taller middle tower.
    cc.moveTo(-50 - g, 40 + g);
    cc.lineTo(-50 - g, -4 - g);
    cc.lineTo(-38, -4 - g);
    cc.lineTo(-38, 4 - g);
    cc.lineTo(-30, 4 - g);
    cc.lineTo(-30, -4 - g);
    cc.lineTo(-20 + g, -4 - g);
    cc.lineTo(-20 + g, -40 - g);
    cc.lineTo(-11, -40 - g);
    cc.lineTo(-11, -32 - g);
    cc.lineTo(-4, -32 - g);
    cc.lineTo(-4, -40 - g);
    cc.lineTo(4, -40 - g);
    cc.lineTo(4, -32 - g);
    cc.lineTo(11, -32 - g);
    cc.lineTo(11, -40 - g);
    cc.lineTo(20 - g, -40 - g);
    cc.lineTo(20 - g, -4 - g);
    cc.lineTo(30, -4 - g);
    cc.lineTo(30, 4 - g);
    cc.lineTo(38, 4 - g);
    cc.lineTo(38, -4 - g);
    cc.lineTo(50 + g, -4 - g);
    cc.lineTo(50 + g, 40 + g);
    cc.closePath();
  };
  celShape(c, body, CASTLE, 40, { line: 4, shift: [-0.12, -0.08] });
  // Door and window.
  c.beginPath();
  c.moveTo(-9, 40);
  c.lineTo(-9, 24);
  c.arc(0, 24, 9, Math.PI, 0);
  c.lineTo(9, 40);
  c.closePath();
  c.fillStyle = "#8a5426";
  c.fill();
  inkStyle(c, 3);
  c.stroke();
  c.beginPath();
  c.arc(0, -16, 5, 0, TAU);
  c.fillStyle = "#8a5426";
  c.fill();
  c.stroke();
  // Flag.
  c.beginPath();
  c.moveTo(0, -40);
  c.lineTo(0, -66);
  inkStyle(c, 3);
  c.stroke();
  c.beginPath();
  c.moveTo(1, -66);
  c.quadraticCurveTo(14, -64, 22, -58);
  c.quadraticCurveTo(12, -54, 1, -52);
  c.closePath();
  c.fillStyle = "#ff5a5f";
  c.fill();
  c.stroke();
  c.restore();
}

/** A beach bucket with a spade stuck in it, next to the castle. */
function bucket(c, x, y) {
  c.save();
  c.translate(x, y);
  c.rotate(0.08);
  // Spade handle behind the rim.
  c.beginPath();
  c.moveTo(8, -6);
  c.lineTo(22, -44);
  inkStyle(c, 9);
  c.stroke();
  c.strokeStyle = "#5fd068";
  c.lineWidth = 4.5;
  c.stroke();
  c.beginPath();
  c.arc(23, -47, 6, 0, TAU);
  c.fillStyle = "#5fd068";
  c.fill();
  inkStyle(c, 3);
  c.stroke();
  // Pail: a tapered body with a lip and a handle.
  celShape(c, function (cc, g) {
    cc.moveTo(-22 - g, -14 - g);
    cc.lineTo(22 + g, -14 - g);
    cc.lineTo(16 + g, 22 + g);
    cc.lineTo(-16 - g, 22 + g);
    cc.closePath();
  }, { base: "#4fa0ff", light: "#9ccaff", shade: "#2f72d0" }, 24, { line: 3.6 });
  c.beginPath();
  c.ellipse(0, -14, 22, 6, 0, 0, TAU);
  c.fillStyle = "#2a5fae";
  c.fill();
  inkStyle(c, 3.4);
  c.stroke();
  c.beginPath();
  c.arc(0, -10, 20, Math.PI * 1.1, Math.PI * 1.9);
  inkStyle(c, 3);
  c.stroke();
  c.fillStyle = "#ffd45c";
  c.beginPath();
  c.arc(-2, 6, 5, 0, TAU);
  c.fill();
  c.restore();
}

function shell(c, x, y, rot, colour) {
  c.save();
  c.translate(x, y);
  c.rotate(rot);
  c.beginPath();
  c.moveTo(0, 9);
  c.bezierCurveTo(-18, 4, -16, -14, 0, -13);
  c.bezierCurveTo(16, -14, 18, 4, 0, 9);
  c.closePath();
  c.fillStyle = colour;
  c.fill();
  inkStyle(c, 3);
  c.stroke();
  c.beginPath();
  c.moveTo(0, 7); c.lineTo(-8, -8);
  c.moveTo(0, 7); c.lineTo(0, -11);
  c.moveTo(0, 7); c.lineTo(8, -8);
  inkStyle(c, 2);
  c.stroke();
  c.restore();
}

function pebble(c, x, y, r) {
  celEllipse(c, x, y, r * 1.3, r, { base: "#b9b2a8", light: "#e2ddd4", shade: "#8c857c" }, { line: 2.6, glint: false });
}

function starfishOnSand(c, x, y) {
  c.beginPath();
  c.ellipse(x + 6, y + 46, 50, 9, 0, 0, TAU);
  c.fillStyle = "rgba(160,110,40,0.3)";
  c.fill();
  if (!CREATURES.starfish) return;
  c.save();
  c.translate(x, y);
  c.rotate(-0.16);
  drawCreature(c, "starfish", 62, { t: 0, noDisc: true, mood: "happy" });
  c.restore();
}

function sparkles(c) {
  var pts = [[198, 40, 9], [26, 112, 7], [236, 176, 8], [486, 40, 10], [150, 330, 7], [470, 352, 8]];
  for (var i = 0; i < pts.length; i++) {
    c.beginPath();
    sparklePath(c, pts[i][0], pts[i][1], pts[i][2], 0);
    c.fillStyle = "#ffffff";
    c.fill();
    inkStyle(c, 1.8, "#2f7fb8");
    c.stroke();
  }
}

// ---- the pieces -------------------------------------------------------------------------

function pieceDrawer(index) {
  return function (c) {
    var spec = PUZZLE_PIECE_LAYOUT[index];
    var poly = puzzlePiecePolygons()[index];
    var ox = (spec.left / 100) * PUZZLE_SIZE, oy = (spec.top / 100) * PUZZLE_SIZE;
    c.save();
    c.translate(-ox, -oy);
    tracePoly(c, poly);
    c.clip();
    var scene = getPuzzleSceneCanvas();
    if (scene) {
      c.drawImage(scene, 0, 0, PUZZLE_SIZE, PUZZLE_SIZE);
      releaseSceneSoon();
    } else {
      drawPuzzleScene(c);
    }
    // Bevel inside the cut: lit along the upper-left edges, shaded along the lower-right ones.
    c.lineJoin = "round";
    c.save();
    c.translate(BEVEL * 0.5, BEVEL * 0.5);
    tracePoly(c, poly);
    c.restore();
    c.strokeStyle = "rgba(255,255,255,0.4)";
    c.lineWidth = BEVEL;
    c.stroke();
    c.save();
    c.translate(-BEVEL * 0.5, -BEVEL * 0.5);
    tracePoly(c, poly);
    c.restore();
    c.strokeStyle = "rgba(23,42,58,0.16)";
    c.lineWidth = BEVEL;
    c.stroke();
    // Ink on the cut itself: a stroke twice as wide, clipped, leaves its inner half.
    tracePoly(c, poly);
    inkStyle(c, EDGE_INK * 2);
    c.stroke();
    c.restore();
  };
}

export var drawPuzzlePiece1 = pieceDrawer(0);
export var drawPuzzlePiece2 = pieceDrawer(1);
export var drawPuzzlePiece3 = pieceDrawer(2);
export var drawPuzzlePiece4 = pieceDrawer(3);

